// Integrytas Group - Field Lineage (FLN)
// The Inspect-mode provenance panel: shows where a clicked field's data comes
// from — source table.column, type, real-vs-custom, a plain-English description,
// derivation notes, and a copyable reference for building reports/views.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Copy, Check, Database, Sparkles, HelpCircle } from 'lucide-react';
import type { FieldLineage, InspectSelection } from '../../types/fln';

interface Props {
  selection: InspectSelection;
  lineage:   FieldLineage | undefined;
  loading:   boolean;
  onClose:   () => void;
}

export function FieldLineagePanel({ selection, lineage, loading, onClose }: Props) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const ref = lineage?.source_table && lineage?.source_column
    ? `${lineage.source_table}.${lineage.source_column}`
    : null;

  const copyRef = async () => {
    if (!ref) return;
    try { await navigator.clipboard.writeText(ref); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  };

  const resolved = !!ref;
  const isCustom = lineage?.is_custom === 'Y';

  return (
    <div className="fixed inset-y-0 right-0 z-[90] flex w-[360px] max-w-[90vw] flex-col border-l border-csm-border bg-white shadow-2xl">
      <header className="flex h-10 shrink-0 items-center justify-between border-b border-csm-border bg-csm-surface-alt px-3">
        <span className="flex items-center gap-1.5 text-csm-sm font-semibold uppercase tracking-wider text-csm-ink-soft">
          <Database size={13} className="text-primary" /> {t('fln.panel.title', 'Data source')}
        </span>
        <button type="button" onClick={onClose} aria-label={t('common.close', 'Close')} className="text-csm-muted hover:text-csm-ink">
          <X size={16} />
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto p-3 text-csm-sm">
        {/* Which field the user clicked */}
        <div className="mb-3">
          <div className="text-csm-base font-semibold text-csm-ink">{selection.label || selection.field_key}</div>
          <div className="font-mono text-csm-2xs text-csm-watermark">{selection.field_key}</div>
        </div>

        {loading && !lineage && (
          <div className="text-csm-xs italic text-csm-watermark">{t('fln.panel.loading', 'Loading source information…')}</div>
        )}

        {!loading && !resolved && (
          <div className="rounded border border-amber-300 bg-amber-50 p-2 text-csm-xs text-amber-800">
            <div className="mb-1 font-medium">{t('fln.panel.undocumented_title', 'Source not yet documented')}</div>
            <p>{t('fln.panel.undocumented_body', 'This field has not been mapped to a data source yet. An administrator can document it on the Field Lineage screen.')}</p>
            {lineage?.tech_hint && (
              <p className="mt-2 flex items-start gap-1 text-amber-700">
                <HelpCircle size={11} className="mt-0.5 shrink-0" /> <span>{lineage.tech_hint}</span>
              </p>
            )}
          </div>
        )}

        {resolved && (
          <>
            {/* The copyable table.column reference — the report-building payload */}
            <div className="mb-3 rounded border border-csm-border bg-csm-surface-alt p-2">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-csm-2xs uppercase tracking-wide text-csm-watermark">{t('fln.panel.reference', 'Source reference')}</span>
                <button type="button" onClick={() => void copyRef()} title={t('fln.panel.copy', 'Copy reference')}
                  className="flex items-center gap-1 rounded border border-csm-border bg-white px-1.5 py-0.5 text-csm-2xs text-csm-muted hover:text-primary">
                  {copied ? <Check size={11} className="text-green-600" /> : <Copy size={11} />}
                  {copied ? t('fln.panel.copied', 'Copied') : t('fln.panel.copy', 'Copy')}
                </button>
              </div>
              <code className="block break-all font-mono text-csm-sm text-csm-ink">{ref}</code>
              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-csm-2xs">
                <span className="rounded bg-primary/10 px-1.5 py-0.5 font-medium text-primary">{lineage?.source_kind_label}</span>
                {lineage?.data_type && <span className="rounded bg-csm-secondary px-1.5 py-0.5 text-csm-ink-soft">{lineage.data_type}</span>}
                {isCustom && (
                  <span className="flex items-center gap-0.5 rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">
                    <Sparkles size={9} /> {t('fln.panel.custom', 'custom field')}
                  </span>
                )}
              </div>
            </div>

            {/* Plain-English description */}
            <Section label={t('fln.panel.description', 'What this is')}>
              {lineage?.description
                ? <p className="text-csm-sm text-csm-ink">{lineage.description}</p>
                : <p className="text-csm-xs italic text-csm-watermark">{t('fln.panel.no_description', 'No description yet.')}</p>}
            </Section>

            {lineage?.derivation_notes && (
              <Section label={t('fln.panel.derivation', 'How it is derived')}>
                <p className="text-csm-sm text-csm-ink">{lineage.derivation_notes}</p>
              </Section>
            )}

            {lineage?.tech_hint && (
              <Section label={t('fln.panel.technical', 'Technical detail')}>
                <p className="font-mono text-csm-2xs text-csm-watermark">{lineage.tech_hint}</p>
              </Section>
            )}
          </>
        )}
      </div>

      <footer className="shrink-0 border-t border-csm-border px-3 py-2 text-csm-2xs text-csm-watermark">
        {t('fln.panel.hint', 'Click any field or column to see its data source.')}
      </footer>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <div className="mb-0.5 text-csm-2xs uppercase tracking-wide text-csm-watermark">{label}</div>
      {children}
    </div>
  );
}
